import { FC, useMemo } from 'react';

import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import FiltersBlock, {
  FiltersBlockProps,
  getSelectedQueryParam,
} from '@components/ui/filters/FiltersBlock';
import PriceRangeFilter from '@components/ui/filters/PriceRangeFilter/PriceRangeFilter';
import NameFilter from '@components/ui/filters/NameFilter/NameFilter';
import SingleSelectionFilter from '@components/ui/filters/SingleSelectionFilter/SingleSelectionFilter';
import ClearAllFilters from '@components/views/components/ClearAllFilters/ClearAllFilters';
import { FlexibleDiv } from '@components/formStyles';
import { isEmptyFilters } from '@components/views/utils';

import TourFilterSection from './TourFilterSection';
import { TourFilterCard } from './TourFilterSectionStyle';

type TourFiltersProps = Pick<FiltersBlockProps, 'applicableFilters'> & {
  isLoadingFilters?: boolean;
  listingTitle?: string;
};

const TourFilters: FC<TourFiltersProps> = ({
  applicableFilters,
  isLoadingFilters,
  listingTitle,
}) => {
  const query = useSearchParamsToQuery();

  const selectedRegions = useMemo(
    () => getSelectedQueryParam(query, 'region'),
    [query]
  );

  const selectedThemes = useMemo(
    () => getSelectedQueryParam(query, 'theme'),
    [query]
  );

  const selectedFeatures = useMemo(
    () => getSelectedQueryParam(query, 'feature'),
    [query]
  );

  const selectedDates = useMemo(
    () => getSelectedQueryParam(query, 'date'),
    [query]
  );

  return (
    <FiltersBlock
      applicableFilters={applicableFilters}
      basePath=''
      isWithShallow={true}
      activeService='tour'
    >
      {({
        filters,
        onRegionChecked: onCheck,
        onlyAvailableHotels,
        handleSingleSelectionFilter,
        keywords,
        setKeywords,
        handleKeywordsFilter,
        handleClearKeywordsFilter,
        minAccPriceValue,
        maxAccPriceValue,
        setMaxAccPriceValue,
        setMinAccPriceValue,
        handlePriceFilter,
        themes,
        handleSelectedThemes,
        features,
        handleSelectedFeatures,
        dates,
        handleSelectedDates,
        departures,
        handleSelectedDepartures,
        transportations,
        handleSelectedTransportations,
        visas,
        handleSelectedVisas,
        specialPeriods,
        handleSelectedSpecialPeriods,
        handleClearAllFilters,
      }) => {
        return (
          <>
            <FlexibleDiv bottom={20}>
              <ClearAllFilters
                onClick={handleClearAllFilters}
                disabled={isLoadingFilters || isEmptyFilters(query)}
                text='Tüm Filtreleri Temizle'
                variant='card'
              />
            </FlexibleDiv>

            {filters?.regions && filters.regions.length > 0 ? (
              <TourFilterSection
                title={listingTitle}
                data={filters.regions}
                selectedItems={selectedRegions}
                handleSelectedItems={onCheck}
                searchable
              />
            ) : null}

            {onlyAvailableHotels?.count ? (
              <TourFilterCard>
                <SingleSelectionFilter
                  title='Sadece yer olan turları göster'
                  subTitle={onlyAvailableHotels?.count || undefined}
                  checked={!!filters.onlyAvailableProperties}
                  mode='controlled'
                  name='status'
                  handleCheckBoxChange={(e) =>
                    handleSingleSelectionFilter(e, 'status')
                  }
                />
              </TourFilterCard>
            ) : null}

            <TourFilterCard>
              <NameFilter
                inputWrapperClassName='listing-hotel-name-filter'
                title='Tur Adı'
                name={keywords}
                setName={setKeywords}
                placeholder='Tur adı ile arayın'
                handleSearch={handleKeywordsFilter}
                handleClearKeywordsFilter={handleClearKeywordsFilter}
                withClear
              />
            </TourFilterCard>

            <TourFilterCard>
              <PriceRangeFilter
                minValue={minAccPriceValue}
                maxValue={maxAccPriceValue}
                setMinValue={setMinAccPriceValue}
                setMaxValue={setMaxAccPriceValue}
                title='Konaklama Fiyatı'
                handleSearch={handlePriceFilter}
              />
            </TourFilterCard>

            <TourFilterSection
              title='Ulaşım Tipi'
              data={transportations || []}
              selectedItems={getSelectedQueryParam(query, 'transportation')}
              handleSelectedItems={handleSelectedTransportations}
            />
            <TourFilterSection
              title='Vize Tipi'
              data={visas || []}
              selectedItems={getSelectedQueryParam(query, 'visa')}
              handleSelectedItems={handleSelectedVisas}
              collapsible
            />
            <TourFilterSection
              title='Özel Dönem'
              data={specialPeriods || []}
              selectedItems={getSelectedQueryParam(query, 'specialPeriod')}
              handleSelectedItems={handleSelectedSpecialPeriods}
              collapsible
            />
            <TourFilterSection
              title='Tur Temaları'
              data={themes || []}
              selectedItems={selectedThemes}
              handleSelectedItems={handleSelectedThemes}
              searchable
              collapsible
            />
            <TourFilterSection
              title='Tur Özellikleri'
              data={features || []}
              selectedItems={selectedFeatures}
              handleSelectedItems={handleSelectedFeatures}
              searchable
              collapsible
            />
            <TourFilterSection
              title='Tur Kalkış Tarihleri'
              data={dates || []}
              selectedItems={selectedDates}
              handleSelectedItems={handleSelectedDates}
              collapsible
            />
            <TourFilterSection
              title='Çıkış Noktası'
              data={departures || []}
              selectedItems={getSelectedQueryParam(query, 'departure')}
              handleSelectedItems={handleSelectedDepartures}
              collapsible
            />
          </>
        );
      }}
    </FiltersBlock>
  );
};

export default TourFilters;
